/**
 * RequireTwoFactor — wrap a page that holds something sensitive (2026-09-26, infrastructure for later).
 *
 *   <RequireTwoFactor><PayrollExportPage /></RequireTwoFactor>
 *
 * - Two-step verification not turned on → a card that says so and links to /security (and back here).
 * - Turned on, but this session hasn't proven a second method within `maxAgeMinutes` (default 30, the
 *   backend guard's default) → a "Confirm it's you" card with the step-up button.
 * - Otherwise renders the children.
 *
 * This only decides what to SHOW. The page's own queries/mutations must also sit behind the backend's
 * `requireTwoFactor` middleware — that is the security; this is the courtesy.
 */
import React from "react";
import { Box, Button, Heading, Icon, Stack, Text } from "@chakra-ui/react";
import { useQuery } from "@apollo/client";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { FiLock, FiShield } from "react-icons/fi";
import { useAuth } from "../../../contexts/AuthContext";
import { MY_TWO_FACTOR_STATUS } from "../queries";
import { SECURE_PAGE_FRESH_MINUTES, isFreshAal2, readStoredAssurance } from "../utils/assurance";
import type { TwoFactorStatus } from "../../../generated/graphql";
import { useStepUp } from "../hooks/useStepUp";
import { AuthError, AuthLoading, AuthSurface, AuthTip, alpha, useAuthTheme } from "./ui";

export type RequireTwoFactorGate = "loading" | "error" | "setup" | "step-up" | "open";

/** Pure: which of the three truths the wrapper shows. Unit-tested. */
export function requireTwoFactorGate(
    status: Pick<TwoFactorStatus, "enabled"> | null | undefined,
    assurance: Parameters<typeof isFreshAal2>[0],
    maxAgeMinutes: number = SECURE_PAGE_FRESH_MINUTES,
    nowSeconds?: number,
): Exclude<RequireTwoFactorGate, "loading" | "error"> {
    if (!status?.enabled) return "setup";
    return isFreshAal2(assurance, maxAgeMinutes, nowSeconds) ? "open" : "step-up";
}

const GateCard: React.FC<{ icon: any; title: string; children: React.ReactNode; action: React.ReactNode }> = ({ icon, title, children, action }) => {
    const t = useAuthTheme();
    return (
        <AuthSurface maxW="560px" mx="auto" my={{ base: 6, md: 12 }} textAlign="center" py={{ base: 8, md: 10 }}>
            <Stack spacing={4} align="center">
                <Box w={14} h={14} borderRadius="2xl" bg={alpha(t.primary, 0.12)} display="flex" alignItems="center" justifyContent="center">
                    <Icon as={icon} boxSize={6} color={t.primary} />
                </Box>
                <Heading size="md" color={t.text}>{title}</Heading>
                <Text fontSize="md" color={t.muted}>{children}</Text>
                {action}
            </Stack>
        </AuthSurface>
    );
};

export const RequireTwoFactor: React.FC<{ children: React.ReactNode; maxAgeMinutes?: number }> = ({ children, maxAgeMinutes = SECURE_PAGE_FRESH_MINUTES }) => {
    const location = useLocation();
    useAuth(); // subscribes: this wrapper re-renders when a step-up changes the session's assurance
    const { requireStepUp, stepUpDialog } = useStepUp({ maxAgeMinutes });
    const { data, loading, error, refetch } = useQuery<{ myTwoFactorStatus: TwoFactorStatus }>(MY_TWO_FACTOR_STATUS, { fetchPolicy: "network-only" });
    const [, tick] = React.useReducer((n: number) => n + 1, 0);

    // A proof goes stale while the page is open: re-check once a minute so the gate closes on time.
    React.useEffect(() => {
        const id = window.setInterval(tick, 60_000);
        return () => window.clearInterval(id);
    }, []);

    if (loading && !data) return <Box maxW="560px" mx="auto" my={12}><AuthLoading label="Checking your two-step verification" cards={1} columns={{ base: 1 }} /></Box>;
    if (error) return <Box maxW="560px" mx="auto" my={12}><AuthError title="Couldn't check your two-step verification" error={error} onRetry={() => refetch()} /></Box>;

    // Read the stored token (always current, even the render right after an adoptToken).
    const gate = requireTwoFactorGate(data?.myTwoFactorStatus, readStoredAssurance(), maxAgeMinutes);
    if (gate === "open") return <>{children}</>;

    const here = `${location.pathname}${location.search}${location.hash}`;
    if (gate === "setup") {
        return (
            <GateCard icon={FiShield} title="This page needs two-step verification" action={
                <AuthTip label="Open your Security page to turn on two-step verification. You'll come back here afterwards.">
                    <Button as={RouterLink} to={`/security?returnTo=${encodeURIComponent(here)}`} colorScheme="blue" size="lg" minH="48px">
                        Turn it on in Security
                    </Button>
                </AuthTip>
            }>
                Pages like this ask you to confirm it's you with two different sign-in methods. Turn on two-step verification on your Security page first.
            </GateCard>
        );
    }
    return (
        <>
            <GateCard icon={FiLock} title="Confirm it's you" action={
                <AuthTip label="Confirm with a second sign-in method (passkey, password, a code or a recovery code). Nothing on your account changes.">
                    <Button colorScheme="blue" size="lg" minH="48px" onClick={() => { void requireStepUp({ force: true, reason: "This page holds sensitive information. Confirm it's you with a second sign-in method." }).then((ok) => { if (ok) tick(); }); }}>
                        Confirm it's you
                    </Button>
                </AuthTip>
            }>
                This page holds sensitive information. Confirm it's you with a second sign-in method to open it.
            </GateCard>
            {stepUpDialog}
        </>
    );
};

export default RequireTwoFactor;
